import { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { MessageSquareQuote } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import {
  getChatDraftSelections,
  notifyChatDraftSelectionsChanged,
  setChatDraftSelections,
} from '@/lib/chat-draft-store'
import {
  normalizeSelectionText,
  parseDraftSelections,
  type ChatSelection,
} from '@/lib/chat/selections'
import { cn } from '@/lib/utils'

/** Selector of the element that wraps one assistant reply (assistant-message.tsx). */
const ASSISTANT_MESSAGE_SELECTOR = '[data-slot="assistant-message"]'

/** Wait this long after the selection last changed before showing the menu. */
const SETTLE_MS = 120
/** Gap between the selection's bounding box and the menu. */
const GAP_PX = 8
/** Approximate height of the native iOS/Android selection callout, kept clear of. */
const TOUCH_CALLOUT_PX = 56

interface Candidate {
  text: string
  truncated: boolean
  messageId?: string
  range: Range
  /** The reply the selection sits in — its bottom edge clears the reply's toolbar on touch. */
  messageEl: Element
}

/** Current selection when it lies entirely inside one element matching `selector`. */
function readSelection(selector: string): Candidate | null {
  const sel = window.getSelection()
  if (!sel || sel.isCollapsed || sel.rangeCount === 0) return null
  const elementOf = (node: Node | null): Element | null =>
    node ? (node.nodeType === Node.ELEMENT_NODE ? (node as Element) : node.parentElement) : null
  const anchor = elementOf(sel.anchorNode)?.closest(selector)
  const focus = elementOf(sel.focusNode)?.closest(selector)
  if (!anchor || anchor !== focus) return null
  const normalized = normalizeSelectionText(sel.toString())
  if (!normalized) return null
  return {
    ...normalized,
    messageId: (anchor as HTMLElement).dataset.messageId,
    range: sel.getRangeAt(0),
    messageEl: anchor,
  }
}

/** Append one selection to a thread's composer draft (the default pill action). */
export function addSelectionToDraft(
  projectId: string,
  threadKey: string,
  selection: ChatSelection
): void {
  const existing = parseDraftSelections(getChatDraftSelections(projectId, threadKey))
  setChatDraftSelections(projectId, threadKey, [...existing, selection])
  notifyChatDraftSelectionsChanged(projectId, threadKey)
}

function coarsePointer(): boolean {
  return typeof window !== 'undefined' && window.matchMedia('(pointer: coarse)').matches
}

export interface SelectionActionMenuProps {
  /** The composer draft the selection is added to (same key as ChatComposer's draftKey). */
  draftKey: { projectId: string; threadKey: string }
  /**
   * Element the selection must sit inside (both ends). Defaults to an
   * assistant reply; the inbox passes its email body (PLAN-084).
   */
  selector?: string
  /** Pill label; defaults to "Add to chat". */
  label?: string
  /**
   * Take the selection instead of writing it to the draft — used where the
   * chat thread does not exist yet (the inbox bubble creates it on open).
   */
  onPick?: (selection: ChatSelection) => void
}

/**
 * Floating "Add to chat" action shown next to text selected inside an
 * assistant reply (board card p33g). Clicking it stores the selection in the
 * thread's composer draft; the composer shows it as a "N selections" chip and
 * sends it as quoted context (see web/lib/chat/selections.ts).
 *
 * Detection is document-wide but only accepts a selection whose both ends sit
 * inside the same `[data-slot="assistant-message"]`, so text that runs into
 * the composer, a user bubble or other chrome never qualifies. On touch
 * devices the menu sits below the selection: the native callout takes the
 * space above it.
 */
export function SelectionActionMenu({
  draftKey,
  selector = ASSISTANT_MESSAGE_SELECTOR,
  label,
  onPick,
}: SelectionActionMenuProps) {
  const { t } = useTranslation('chat')
  const [candidate, setCandidate] = useState<Candidate | null>(null)
  const [pos, setPos] = useState<{ left: number; top: number; below: boolean } | null>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const timer = useRef<number | null>(null)

  const place = useCallback((range: Range, messageEl: Element) => {
    const rect = range.getBoundingClientRect()
    if (rect.width === 0 && rect.height === 0) {
      setPos(null)
      return
    }
    const below = coarsePointer()
    const width = buttonRef.current?.offsetWidth ?? 160
    const height = buttonRef.current?.offsetHeight ?? 36
    const left = Math.min(
      Math.max(GAP_PX, rect.left + rect.width / 2 - width / 2),
      window.innerWidth - width - GAP_PX
    )
    // Touch: below the whole reply (a selection on its last line would
    // otherwise put the pill on the reply's own action row); when that is
    // off-screen, above the selection with room for the native callout.
    const bottomEdge = below
      ? Math.max(rect.bottom, messageEl.getBoundingClientRect().bottom)
      : rect.bottom
    let top = below ? bottomEdge + GAP_PX : rect.top - height - GAP_PX
    if (!below && top < GAP_PX) top = rect.bottom + GAP_PX
    if (below && top + height > window.innerHeight - GAP_PX) {
      top = Math.max(GAP_PX, rect.top - height - GAP_PX - TOUCH_CALLOUT_PX)
    }
    setPos({ left, top, below })
  }, [])

  // While the mouse button is down the selection is still being dragged out;
  // the menu waits for mouseup so it does not chase the cursor.
  const dragging = useRef(false)
  useEffect(() => {
    const settle = () => {
      if (timer.current) window.clearTimeout(timer.current)
      timer.current = window.setTimeout(() => {
        timer.current = null
        const next = dragging.current ? null : readSelection(selector)
        setCandidate(next)
        if (next) place(next.range, next.messageEl)
        else setPos(null)
      }, SETTLE_MS)
    }
    const onMouseDown = (e: MouseEvent) => {
      if (e.button !== 0 || buttonRef.current?.contains(e.target as Node)) return
      dragging.current = true
    }
    const onMouseUp = () => {
      dragging.current = false
      settle()
    }
    document.addEventListener('selectionchange', settle)
    document.addEventListener('mousedown', onMouseDown)
    document.addEventListener('mouseup', onMouseUp)
    document.addEventListener('touchend', settle)
    document.addEventListener('keyup', settle)
    return () => {
      if (timer.current) window.clearTimeout(timer.current)
      document.removeEventListener('selectionchange', settle)
      document.removeEventListener('mousedown', onMouseDown)
      document.removeEventListener('mouseup', onMouseUp)
      document.removeEventListener('touchend', settle)
      document.removeEventListener('keyup', settle)
    }
  }, [place, selector])

  // Follow the selection while the thread scrolls or the window resizes.
  useEffect(() => {
    if (!candidate) return
    const follow = () => place(candidate.range, candidate.messageEl)
    window.addEventListener('scroll', follow, { capture: true, passive: true })
    window.addEventListener('resize', follow)
    return () => {
      window.removeEventListener('scroll', follow, { capture: true })
      window.removeEventListener('resize', follow)
    }
  }, [candidate, place])

  // Measure once the button exists so the first placement is centred.
  useEffect(() => {
    if (candidate && pos) place(candidate.range, candidate.messageEl)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [candidate])

  const add = () => {
    if (!candidate) return
    const selection: ChatSelection = {
      id: crypto.randomUUID(),
      text: candidate.text,
      truncated: candidate.truncated,
      ...(candidate.messageId ? { messageId: candidate.messageId } : {}),
    }
    if (onPick) {
      onPick(selection)
    } else {
      addSelectionToDraft(draftKey.projectId, draftKey.threadKey, selection)
    }
    window.getSelection()?.removeAllRanges()
    setCandidate(null)
    setPos(null)
  }

  if (!candidate || !pos || typeof document === 'undefined') return null

  return createPortal(
    <button
      ref={buttonRef}
      type="button"
      data-slot="selection-menu"
      data-side={pos.below ? 'bottom' : 'top'}
      // mousedown would collapse the selection before click fires.
      onMouseDown={(e) => e.preventDefault()}
      onClick={add}
      style={{ position: 'fixed', left: pos.left, top: pos.top }}
      className={cn(
        // Opaque in both themes at rest and on hover: the shared `floating`
        // surface with a translucent hover looked washed out in light mode.
        'bg-popover text-popover-foreground border-border/60 hover:bg-accent hover:text-accent-foreground border',
        'animate-in fade-in zoom-in-95 z-50 flex h-9 items-center gap-2 rounded-full px-3.5 text-[13px] font-medium shadow-lg shadow-black/10 duration-150 select-none dark:shadow-black/40',
        'active:scale-[0.98]'
      )}
    >
      <MessageSquareQuote className="size-4 text-blue-500 dark:text-blue-400" />
      {label ?? t('composer.addToChat')}
    </button>,
    document.body
  )
}
